import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { base_url } from '../../utils/config';
import {
  AiFillHeart,
  AiOutlineHeart,
  AiOutlineShoppingCart
} from 'react-icons/ai';
import { FaFemale } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { getCustomerActivity } from '../../store/reducers/customerActivitySlice';
import {
  add_to_card,
  add_to_wishlist,
  remove_wishlist,
  get_wishlist_products,
  messageClear,
} from '../../store/reducers/cardReducer';
import {
  add_viewed_product,
  add_cart_activity,
  add_wishlist_activity,
  add_swap_activity
} from '../../store/reducers/homeReducer';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Ratings from '../Ratings';
import { useEnrichProducts } from './enrichedProducts';
import '../../styles/commonfeminine.css';

// Helper: compute average rating
const getAverageRating = (product) => {
  if (!product?.rating_review?.length) return 0;
  let totalCount = 0, totalSum = 0;
  product.rating_review.forEach((rev, i) => {
    const count = rev.sum || 0;
    totalCount += count;
    totalSum += count * (5 - i);
  });
  return totalCount ? (totalSum / totalCount).toFixed(1) : 0;
};

const ContinueShopping = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useSelector(s => s.auth);
  const { wishlist, successMessage, errorMessage } = useSelector(s => s.card);
  const { customerActivities } = useSelector(s => s.customerActivity);
  const [filtered, setFiltered] = useState([]);

  // Load wishlist & activity
  useEffect(() => {
    if (userInfo) {
      dispatch(get_wishlist_products(userInfo.id));
      dispatch(getCustomerActivity(userInfo.id));
    }
  }, [dispatch, userInfo]);

  // refresh wishlist on success (toast handled by parent)
  useEffect(() => {
    if (successMessage && userInfo) {
      dispatch(get_wishlist_products(userInfo.id));
    }
  }, [successMessage, dispatch, userInfo]);

  // Filter "continue" products
  useEffect(() => {
    if (!customerActivities?.length) return;
    const seen = new Set(), uniq = [];
    customerActivities
      .filter(a => a.timesViewed > 2 || a.timesWishlisted > 0 || a.timesCarted > 0)
      .map(a => a.productId)
      .filter(p => p?._id)
      .forEach(p => {
        if (!seen.has(p._id)) {
          seen.add(p._id);
          uniq.push(p);
        }
      });
    setFiltered(uniq);
  }, [customerActivities]);

  // Enrich with full product data
  const enriched = useEnrichProducts(filtered);

  const addCart = (id) => {
    if (!userInfo) return navigate('/login');
    dispatch(add_to_card({ userId: userInfo.id, quantity: 1, productId: id }));
    // Track cart activity
    dispatch(add_cart_activity({ userId: userInfo.id, productId: id, source: 'ContinueShopping' }));
  };

  const toggleWish = (p, e) => {
    e.stopPropagation();
    if (!userInfo) return navigate('/login');
    const exists = wishlist.some(w => w.productId === p._id);
    if (exists) {
      const entry = wishlist.find(w => w.productId === p._id);
      dispatch(remove_wishlist(entry._id));
    } else {
      dispatch(add_to_wishlist({
        userId:    userInfo.id,
        productId: p._id,
        name:      p.name,
        price:     p.price,
        image:     p.images[0],
        discount:  p.discount,
        slug:      p.slug || p._id.toString(),
      }));
      // Track wishlist activity
      dispatch(add_wishlist_activity({ userId: userInfo.id, productId: p._id, source: 'ContinueShopping' }));
    }
  };

  const addSwap = (p, e) => {
    e.stopPropagation();
    if (!userInfo) return navigate('/login');
    // Track swap activity
    dispatch(add_swap_activity({ userId: userInfo.id, productId: p._id, source: 'ContinueShopping' }));
  };

  const handleProductClick = (productId) => {
    navigate(`/product/details/${productId}`);
    // View tracking is handled in Details page to avoid duplicates
  };

  if (!enriched.length) {
    return (
      <div className="w-[85%] mx-auto py-10 text-center text-secondary">
        No recent items to continue shopping.
      </div>
    );
  }

  return (
    <div className="w-[85%] mx-auto py-10">
      {/* heading */}
      <div className="mb-8 flex justify-center">
        <h2
          className="text-2xl font-bold text-center"
          style={{
            color: 'var(--color-text-primary)',
            textDecoration: 'underline',
            textUnderlineOffset: '4px'
          }}
        >
          Continue Shopping
        </h2>
      </div>

      <div className="flex gap-6 overflow-x-auto hide-scrollbar pb-4">
        {enriched.map((p, i) => {
          const avg = getAverageRating(p);
          const discounted = p.discount
            ? p.price - Math.floor(p.price * p.discount / 100)
            : p.price;
          const fav = wishlist.some(w => w.productId === p._id);

          return (
            <div
              key={p._id || i}
              onClick={() => handleProductClick(p._id)}
              className="card min-w-[200px] max-w-[200px] flex-shrink-0 flex flex-col"
            >
              {/* image + badges */}
              <div className="relative aspect-[3.4/4] overflow-hidden">
                <img
                  src={p.images[0]}
                  alt={p.name}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={e => toggleWish(p, e)}
                  className="absolute top-2 left-2 p-1 rounded-full"
                  style={{
                    background: 'var(--color-card-bg)',
                    border:     `1px solid var(--color-border)`,
                    color:      fav ? 'var(--color-primary)' : 'var(--color-border)',
                  }}
                >
                  {fav ? <AiFillHeart size={18}/> : <AiOutlineHeart size={18}/>}
                </button>
                {p.discount > 0 && (
                  <div
                    className="absolute bottom-2 left-2 px-2 py-1 bg-white bg-opacity-80 rounded text-xs font-semibold"
                    style={{ color: 'var(--color-primary)' }}
                  >
                    -{p.discount}%
                  </div>
                )}
              </div>

              {/* details */}
              <div className="p-3 flex-1 flex flex-col justify-between bg-white">
                <div>
                  <h3 className="text-base font-semibold line-clamp-1" style={{ color: 'var(--color-text-primary)' }}>
                    {p.name}
                  </h3>
                  {p.shopName && (
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {p.shopName}
                    </p>
                  )}
                  <div className="mt-2 flex items-baseline gap-2">
                    {p.discount > 0 ? (
                      <>
                        <span className="line-through text-sm text-gray-500">₹{p.price}</span>
                        <span className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>
                          ₹{discounted}
                        </span>
                      </>
                    ) : (
                      <span className="text-lg font-bold" style={{ color: 'var(--color-primary)' }}>
                        ₹{p.price}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center mt-1 text-xs text-gray-600">
                    <Ratings ratings={parseFloat(avg)} />
                    <span className="ml-2">({p.totalReview || 0})</span>
                  </div>
                </div>
              </div>

              {/* action pill */}
              <div
                className="flex h-12 w-full border-t rounded-b-lg overflow-hidden"
                style={{ borderColor: 'var(--color-border)' }}
              >
                {/* Add to Cart */}
                <button
                  onClick={e => { e.stopPropagation(); addCart(p._id); }}
                  className="button-primary flex-1 flex flex-col items-center justify-center rounded-bl-lg"
                >
                  <span className="font-semibold text-sm">Add to Cart</span>
                  <AiOutlineShoppingCart className="text-xl mt-1 font-bold" />
                </button>

                {/* Virtual Swap */}
                <button
                  onClick={e => addSwap(p, e)}
                  className="button-secondary flex-1 flex flex-col items-center justify-center rounded-br-lg border-l"
                  style={{
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-primary)',      /* terracotta text */
                    background: 'var(--color-accent)',   /* sage bg */
                  }}
                >
                  <span className="font-semibold text-sm">Virtual Swap</span>
                  <FaFemale className="text-xl mt-1 font-bold" />
                </button>
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ContinueShopping;
